// How many creatures a spell may touch, read out of its own text.
//
// The inventory carries name, level, handler, duration, range, area and save —
// but no target count, for any of the 1,168 spells. "Up to six creatures" only
// exists in D&D Beyond's prose, which the connector already fetches and caches
// per session for a template's GM Notes. So the cap is parsed from that same
// description rather than kept in a hand-written table: a table would be another
// pile of rules data to get wrong, and this session has twice shown that wrong
// rules data is worse than none (Evard's saving throw, Ice Storm's damage type).
//
// Deliberately narrow: ONLY the "up to N <creatures>" form is read. It is the
// unambiguous multi-target statement, and it cannot be confused with a distance
// ("up to 60 feet") or a duration ("up to 10 minutes") because a creature noun
// must follow the number. Single-target spells are a separate job and are left
// uncapped here on purpose.
import { ddbSpellText } from "./ddbSpells";

const WORD: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12 };

/** the nouns a spell uses for the things it targets */
const NOUN = "(?:willing |friendly |allied |hostile |other |different |additional )*(?:creature|target|ally|allies|beast|humanoid|corpse|object|ooze|plant)";

/** The cap stated in a spell's own text, or null when it states none. */
export function targetCapFrom(html: string): number | null {
  const text = String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ");
  // The FIRST such phrase is the targeting sentence; a later one is usually the
  // upcast clause talking about additional creatures, which is not the base cap.
  const m = new RegExp(`\\bup to (\\d{1,2}|${Object.keys(WORD).join("|")}) ${NOUN}s?\\b`, "i").exec(text);
  if (!m) return null;
  const n = /^\d+$/.test(m[1]) ? Number(m[1]) : WORD[m[1].toLowerCase()];
  return Number.isFinite(n) && n > 0 && n <= 20 ? n : null;
}

/* HOW THE CAP GROWS WITH THE SLOT, read from the same text.

   targetCapFrom deliberately ignores the upcast sentence, and said so: "a later
   one is usually the upcast clause talking about additional creatures, which is
   not the base cap". That was right for the base cap and left the growth unread,
   which is the gap a hand-written table was filling. The prose states it plainly
   and in one standard form, so it can be read here instead:

       You can target one additional creature for each spell slot level above 1.

   Same discipline as the cap: ONLY the unambiguous form, a creature noun must
   follow the number, and anything unrecognised returns null rather than a guess.
   A spell that grows in some other way is better left uncapped than capped wrongly. */
export interface TargetGrowth { per: number; above: number }
export function targetGrowthFrom(html: string): TargetGrowth | null {
  const text = String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ");
  const m = new RegExp(`target (\\d{1,2}|${Object.keys(WORD).join("|")}) additional ${NOUN}s?,? for each (?:spell )?slot level above (\\d{1,2})`, "i").exec(text);
  if (!m) return null;
  const per = /^\d+$/.test(m[1]) ? Number(m[1]) : WORD[m[1].toLowerCase()];
  const above = Number(m[2]);
  if (!Number.isFinite(per) || per <= 0 || per > 10) return null;
  if (!Number.isFinite(above) || above < 0 || above > 9) return null;
  return { per, above };
}

/** The cap at a given slot: the base, plus the growth the text states. */
export function capAtSlot(base: number | null, growth: TargetGrowth | null, slot?: number): number | null {
  if (base === null) return null;
  if (!growth || !Number.isFinite(Number(slot))) return base;
  const extra = Math.max(0, Number(slot) - growth.above);
  return base + growth.per * extra;
}
/** Caps already worked out this session, so the dialog asks the connector once per spell. */
const CACHE = new Map<string, number | null>();

/**
 * The cap for a spell, via the connector's cached D&D Beyond text.
 * Null means "no cap known" and must be treated as no limit rather than as zero:
 * a spell the account does not own returns no text at all, and the dialog has to
 * keep working for it.
 */
export async function spellTargetCap(name: string, edition = "2024"): Promise<number | null> {
  const key = `${String(name || "").trim().toLowerCase()}@${edition}`;
  if (CACHE.has(key)) return CACHE.get(key)!;
  let cap: number | null = null;
  try {
    const t = await ddbSpellText(name, edition);
    cap = t.owned && t.description ? targetCapFrom(t.description) : null;
  } catch { cap = null; }
  CACHE.set(key, cap);
  return cap;
}

/** For tests and callers that already hold the text. */
export const __clearTargetCapCache = () => CACHE.clear();

/* ---- what the dialog does with a cap ----
   Kept here rather than inside the tracker component so it can be tested
   without a DOM, and so a second surface (the Compass canvas, one day) can
   enforce the same rule without lifting it out of some JSX. */

/** Is the cap already met, so nothing further may be ticked? */
export const capReached = (targets: { checked: boolean }[], cap: number | null): boolean =>
  cap !== null && targets.filter((t) => t.checked).length >= cap;

/** Tick everyone the test picks, stopping at the cap. The All / Foes / Allies
 *  buttons are a shortcut, not permission to exceed what the spell reaches, and
 *  a null cap means no limit rather than none allowed. */
export function capPick<T extends { checked: boolean }>(targets: T[], want: (t: T) => boolean, cap: number | null): T[] {
  let left = cap === null ? Infinity : Math.max(0, cap);
  return targets.map((t) => {
    const take = want(t) && left > 0;
    if (take) left -= 1;
    return { ...t, checked: take };
  });
}
